import { useMemo } from "react";

import {
  BasicLifeBenefits,
  GeneralInformation,
  PlanRates,
  BasicLifeCoverage,
  BasicLifePlan,
} from "../../constants/aquafina";
import CollapsableSection from "../sections/collapsable-section";
import { RangeIterator } from "../../utils";
import ContributionType from "../sections/contribution-type-section";
import Rates from "../rates";
import {
  CoverageDividerRow,
  CoverageRow,
  CoverageArrowRow,
  CoverageHeaderRow,
  NotProvidedRow,
} from "../table/rows";
import { CoverageHeaderCell, CoverageCell } from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: BasicLifeCoverage;
  displayV4Fields: boolean;
};

function BasicLife({ coverage, viewMode, displayV4Fields }: Props) {
  const state = useMemo(() => {
    const benefitDetails: BasicLifeBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const rates: PlanRates[] = [];
    const plans: BasicLifePlan[] = [];
    let inconsistentRateData = false;

    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return { benefitDetails, generalInfo, plans, rates, inconsistentRateData };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo
          data={state.generalInfo}
          hideParticipationRequirement
        />
      )}
      <BenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
}

type BenefitDetailsProps = {
  benefits: BasicLifeBenefits[];
  displayV4Fields: boolean;
};

const BenefitDetails = ({ benefits, displayV4Fields }: BenefitDetailsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <BenefitDetailsData benefits={benefits} displayV4Fields={displayV4Fields} />
  </CollapsableSection>
);

const BenefitDetailsData = ({
  benefits,
  displayV4Fields,
}: BenefitDetailsProps) => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  const { hasSpouse, hasChild } = benefits.reduce(
    (acc, b) => {
      if (b.dependentBenefits?.spouse) {
        acc.hasSpouse = true;
      }
      if (b.dependentBenefits?.child) {
        acc.hasChild = true;
      }
      return acc;
    },
    { hasSpouse: false, hasChild: false },
  );

  return (
    <>
      <CoverageHeaderRow isWhite={true}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageHeaderCell label={"Employee"} key={index} />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Employee Benefits">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].employeeBenefit} key={index} />
        ))}
      </CoverageRow>
      {hasSpouse || hasChild ? (
        <CoverageDividerRow
          label="Dependent Benefits"
          totalPlans={benefits.length}
          isWhite={true}
        />
      ) : null}
      {hasSpouse ? (
        <CoverageArrowRow label="Spouse">
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].dependentBenefits?.spouse}
              key={index}
            />
          ))}
        </CoverageArrowRow>
      ) : null}
      {hasChild ? (
        <CoverageArrowRow label="Child" isWhite={true}>
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].dependentBenefits?.child}
              key={index}
            />
          ))}
        </CoverageArrowRow>
      ) : null}
      <CoverageRow label="Guarantee Issue" isWhite={true}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].guaranteeIssue} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="AD&D Included">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].isAdndIncluded} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Portability" isWhite={true}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].portability} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Conversion">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isConversionIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Accelerated Death Benefit" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isAcceleratedDeathBenefitIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Waiver of Premium">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isWaiverOfPremiumIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageRow label="Age Reduction Schedule" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].ageReductionSchedule}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Definition of Earnings">
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].definitionOfEarnings}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
    </>
  );
};

export default BasicLife;
